import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useEffect } from "react";
import { toast } from "react-hot-toast";

import Button from "components/common/Button";
import TextInput from "components/common/InputField/TextInput";
import { getTextFieldProps } from "helpers/getTextFieldProps";
import CustomToast from "components/common/CustomToast";
import SpaceGuestBadge from "../SpaceGuestBadge";
import Avatar from "components/common/Avatar";
import { useSpaceGuestAccept, useSpaceGuestInvitePreview } from "hooks/spaceHooks";
import { generatePath, useNavigate, useSearchParams } from "react-router-dom";
import * as UrlConstants from "constants/urls";
import { useAppSelector } from "hooks/storeHooks";
import SpaceGuestAuthLayout from "../SpaceGuestAuthLayout";

export default function SpaceGuestJoin() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();

    const { spaceGuestAcceptedSuccess } = useAppSelector((state) => state.space);
    const [spaceGuestAcceptMutation, spaceGuestAcceptMutationResult] = useSpaceGuestAccept();
    const getSpaceGuestInvitePreviewQuery = useSpaceGuestInvitePreview({
        path: { token },
        options: { skip: !token },
    });

    const formik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            role: "",
            token,
        },
        validationSchema: Yup.object({
            firstName: Yup.string().required("First name is required").trim(),
            lastName: Yup.string().required("Last name is required").trim(),
            role: Yup.string().required("Job Title is required").trim(),
        }),
        onSubmit: async (values) => {
            try {
                await spaceGuestAcceptMutation(values);
                toast.custom((t) => (
                    <CustomToast t={t} message="Guest joined successfully" type="success" />
                ));
            } catch (error) {
                toast.custom((t) => <CustomToast t={t} message={error?.message} type="error" />);
            }
        },
    });

    console.log({ getSpaceGuestInvitePreviewQuery: getSpaceGuestInvitePreviewQuery?.data });

    const brandName = getSpaceGuestInvitePreviewQuery?.data?.brand?.name || "....";
    const spaceName = getSpaceGuestInvitePreviewQuery?.data?.name || "....";
    const inviteBy = getSpaceGuestInvitePreviewQuery?.data?.invitedBy?.name || "....";
    const inviteByImage = getSpaceGuestInvitePreviewQuery?.data?.invitedBy?.avatar;
    const avatar = getSpaceGuestInvitePreviewQuery?.data?.brand?.avatar;

    const LogoContainer = () => {
        return (
            <div className="w-[52px] h-[52px] border border-slate-200 rounded-lg p-[2px] flex items-center justify-center">
                <img src={avatar} alt="" className="w-full h-full rounded-md object-cover" />
            </div>
        );
    };

    useEffect(() => {
        if (!!spaceGuestAcceptedSuccess) {
            navigate(
                generatePath(UrlConstants.SPACES_GUEST_DETAILS, {
                    spaceId: spaceGuestAcceptedSuccess?.data?.space?._id,
                })
            );
        }
    }, [spaceGuestAcceptedSuccess]);

    return (
        <SpaceGuestAuthLayout>
            <form
                onSubmit={formik.handleSubmit}
                className="w-full max-w-[475px] h-fit  bg-white rounded-[8px] overflow-hidden"
            >
                <div className="space-y-[24px] p-[32px] flex flex-col items-center justify-center">
                    <div>
                        <LogoContainer />
                        <div className="mt-1" />
                        <SpaceGuestBadge />
                    </div>

                    <div>
                        <p className="text-sm font-rocGroteskMedium text-center">{brandName}</p>
                        <p className="text-sm text-slate-400 font-rocGroteskMedium text-center mt-2">
                            You’ve been invited to join the{" "}
                            <b className="text-black">{spaceName}</b> space. Please provide your
                            first and last name so we can complete your profile and get you set up.
                        </p>
                    </div>

                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <TextInput
                                {...getTextFieldProps(formik, "firstName")}
                                inputPlaceholder="First name"
                                disableNativeValidation
                                name="firstName"
                                inputClassName="!w-full"
                            />
                            <TextInput
                                {...getTextFieldProps(formik, "lastName")}
                                inputPlaceholder="Last name"
                                disableNativeValidation
                                name="lastName"
                                inputClassName="!w-full"
                            />
                        </div>
                        <TextInput
                            {...getTextFieldProps(formik, "role")}
                            inputPlaceholder="Job Title"
                            disableNativeValidation
                            name="role"
                            btnClassname="col-span-2"
                            inputClassName="!w-full"
                        />
                    </div>

                    <div className="w-full">
                        <Button
                            disabled={!token}
                            btnText="Join Space"
                            type="submit"
                            isLoading={spaceGuestAcceptMutationResult.isLoading}
                            btnClassname="!w-full"
                        />
                        <Button
                            btnText="No, Cancel"
                            type="button"
                            onClick={() => {
                                navigate("/");
                            }}
                            btnClassname="!bg-white !border !border-[#F4F5F7] !w-full !text-gm-50 mt-2"
                        />
                    </div>
                </div>
                <div className="px-[24px] py-[8px] gap-[8px] flex items-center justify-center bg-[#F8FAFC] border-t border-[#E2E8F0]">
                    <p className="text-sm text-slate-400 font-rocGroteskMedium text-center">
                        This invitation was sent by
                    </p>
                    <div className="flex items-center gap-1">
                        <Avatar src={inviteByImage} className="w-[16px] h-[16px]" />
                        <p className="text-sm font-rocGroteskMedium text-center text-black">
                            {inviteBy}
                        </p>
                    </div>
                </div>
            </form>
        </SpaceGuestAuthLayout>
    );
}
